import { StateCreator } from "zustand";
import { supabase } from "@/lib/supabase";
import {
	JobWithShare,
	SupabaseShareEntityViewEnum,
} from "@/lib/supabase/supabaseTypes";
import { JobStatusEnum } from "@/lib/types/job";
import { Logger } from "@/lib/logger/Logger";
import { useCentralStore } from "../Central";
import { produce } from "immer";
import { JobStoreUnion } from ".";
import { JobDocumentTypeEnum } from "../../pages/job-page/job-document/job-document.types";
import { getJobListEntry, getJobStage, getStatusAndStageFromId } from "./utils";
import { showNotification } from "../Central/selectors";

// One might argue that this should be part of the client store. Why?
// - Because the it's the list of jobs for the client.
// - Because the job store focused on the currently selected job and is impacted by the job route.
// However, the job list tracks the status and whether a job is shared. Therefore, it should be mutated by the job store.

export type JobStage =
	| JobDocumentTypeEnum.Quotation
	| JobDocumentTypeEnum.DeliveryNote
	| JobDocumentTypeEnum.CreditNote
	| "empty";

interface JobListEntryWithStages {
	stages: true;
	data: Record<JobStage, { jobCount: number; jobs: JobWithShare[] }>;
}

interface JobListEntryFlat {
	stages: false;
	data: { jobCount: number; jobs: JobWithShare[] };
}

type JobListEntry = JobListEntryWithStages | JobListEntryFlat;

interface JobListState {
	jobList: Record<JobStatusEnum, JobListEntry>;
	selectedJobStatus: JobStatusEnum | null;
	selectedJobStage: JobStage | null;
	pinnedJobs: Record<string, JobWithShare>;
}

export const initialJobListState: JobListState = {
	jobList: {
		[JobStatusEnum.BOOKED_SINGLE]: {
			stages: false,
			data: { jobCount: 0, jobs: [] },
		},
		[JobStatusEnum.IN_PROGRESS]: {
			stages: true,
			data: {
				["empty"]: { jobCount: 0, jobs: [] },
				[JobDocumentTypeEnum.Quotation]: { jobCount: 0, jobs: [] },
				[JobDocumentTypeEnum.DeliveryNote]: { jobCount: 0, jobs: [] },
				[JobDocumentTypeEnum.CreditNote]: { jobCount: 0, jobs: [] },
			},
		},
		[JobStatusEnum.COMPLETED]: {
			stages: false,
			data: { jobCount: 0, jobs: [] },
		},
		[JobStatusEnum.REQUESTED]: {
			stages: false,
			data: { jobCount: 0, jobs: [] },
		},
		[JobStatusEnum.BOOKED_MONTHLY]: {
			stages: false,
			data: { jobCount: 0, jobs: [] },
		},
		[JobStatusEnum.ARCHIVED]: {
			stages: false,
			data: { jobCount: 0, jobs: [] },
		},
		[JobStatusEnum.PAID_SINGLE]: {
			stages: false,
			data: { jobCount: 0, jobs: [] },
		},
		[JobStatusEnum.PAID_MONTHLY]: {
			stages: false,
			data: { jobCount: 0, jobs: [] },
		},
	},
	selectedJobStatus: null,
	selectedJobStage: null,
	pinnedJobs: {},
};

export interface JobListSlice extends JobListState {
	fetchJobsByStatus: (status: JobStatusEnum) => Promise<void>;
	changeSelectedJobStatusOrStage: (
		status: JobStatusEnum | null,
		stage: JobStage | null
	) => void;
	// USED EXCLUSIVELY to update fields displayed in the job list
	updateJobInList: (jobId: number, field: "title" | "code" | "patient_id" | "is_paid_by_patient", value: any) => void; // NOT USED TO UPDATE JOB STATUS
	deleteJobFromList: (jobId: number) => void;
	addJobToList: (job: JobWithShare) => void;
	pinJob: (job: JobWithShare) => void;
	unpinJob: (jobId: number) => void;
	unpinAllJobs: () => void;
}

export const createJobListStore: StateCreator<
	JobStoreUnion,
	[],
	[],
	JobListSlice
> = (set, get) => ({
	...initialJobListState,
	fetchJobsByStatus: async (status: JobStatusEnum) => {
		const searchConfig = useCentralStore.getState().searchConfig;

		let year = searchConfig?.statuses?.[status] ?? 0;
		const clientId = useCentralStore.getState().clientId;

		if (!clientId) return;

		if (year === 0) {
			year = 100;
		}

		const targetDate = new Date();
		targetDate.setDate(targetDate.getDate() - 365 * year);

		const { data, error } = await supabase
			.from(SupabaseShareEntityViewEnum.JOBS_WITH_SHARES)
			.select("*")
			.eq("status", status)
			.eq("client_id", clientId)
			.order("created_at", { ascending: false })
			.gte("created_at", targetDate.toISOString());
		if (error) {
			set(
				produce((state) => {
					if (status === JobStatusEnum.IN_PROGRESS) {
						state.jobList[status] = {
							stages: true,
							data: {
								["empty"]: { jobCount: 0, jobs: [] },
								[JobDocumentTypeEnum.Quotation]: {
									jobCount: 0,
									jobs: [],
								},
								[JobDocumentTypeEnum.DeliveryNote]: {
									jobCount: 0,
									jobs: [],
								},
								[JobDocumentTypeEnum.CreditNote]: {
									jobCount: 0,
									jobs: [],
								},
							},
						};
					} else {
						state.jobList[status] = {
							stages: false,
							data: { jobCount: 0, jobs: [] },
						};
					}
				})
			);
			Logger.error(error, {}, "Error fetching jobs by status");
			return;
		}

		let jobListEntry: JobListEntry;
		if (status === JobStatusEnum.IN_PROGRESS) {
			jobListEntry = {
				stages: true,
				data: {
					["empty"]: getJobListEntry(data, "empty"),
					[JobDocumentTypeEnum.Quotation]: getJobListEntry(
						data,
						JobDocumentTypeEnum.Quotation
					),
					[JobDocumentTypeEnum.DeliveryNote]: getJobListEntry(
						data,
						JobDocumentTypeEnum.DeliveryNote
					),
					[JobDocumentTypeEnum.CreditNote]: getJobListEntry(
						data,
						JobDocumentTypeEnum.CreditNote
					),
				},
			};
		} else if (status === JobStatusEnum.REQUESTED) {
			jobListEntry = {
				stages: false,
				data: {
					jobCount: data.length,
					jobs: data,
				},
			};
		} else {
			jobListEntry = {
				stages: false,
				data: { jobCount: data.length, jobs: data },
			};
		}

		set(
			produce((state) => {
				state.jobList[status] = jobListEntry;
			})
		);
	},
	changeSelectedJobStatusOrStage: (
		status: JobStatusEnum | null,
		stage: JobStage | null
	) => {
		set({
			selectedJobStatus: status,
			selectedJobStage: stage,
		});

		if (status) get().fetchJobsByStatus(status);
	},
	pinJob: (job: JobWithShare) => {
		set(
			produce((state) => {
				if (!job.id) return;
				state.pinnedJobs[job.id] = job;
			})
		);
	},
	unpinJob: (jobId: number) => {
		set(
			produce((state) => {
				if (!jobId) return;
				delete state.pinnedJobs[jobId];
			})
		);
	},
	unpinAllJobs: () => {
		set(
			produce((state) => {
				state.pinnedJobs = {};
			})
		);
	},
	updateJobInList: (jobId: number, field: "title" | "code" | "patient_id" | "is_paid_by_patient", value: any) => {
		if (!jobId) return;
		const jobList = get().jobList;
		const { status, stage } = getStatusAndStageFromId(jobId);

		if (!status) return;

		let jobIndex: number = -1;

		if (jobList[status].stages && stage) {
			jobIndex = jobList[status].data[stage].jobs.findIndex(
				(j) => j.id === jobId
			);
		} else if (!jobList[status].stages) {
			jobIndex = jobList[status].data.jobs.findIndex(
				(j) => j.id === jobId
			);
		}

		set(
			produce((state) => {
				if (state.jobList[status].stages && stage) {
					state.jobList[status].data[stage].jobs[jobIndex] = {
						...state.jobList[status].data[stage].jobs[jobIndex],
						[field]: value,
					};
				} else if (!state.jobList[status].stages) {
					state.jobList[status].data.jobs[jobIndex] = {
						...state.jobList[status].data.jobs[jobIndex],
						[field]: value,
					};
				}
			})
		);
	},
	deleteJobFromList: (jobId: number) => {
		const jobList = get().jobList;
		const { status, stage } = getStatusAndStageFromId(jobId);

		if (!status) return;

		let jobIndex: number = -1;
		if (jobList[status].stages && stage) {
			jobIndex = jobList[status].data[stage].jobs.findIndex(
				(j) => j.id === jobId
			);
		} else if (!jobList[status].stages) {
			jobIndex = jobList[status].data.jobs.findIndex(
				(j) => j.id === jobId
			);
		}

		set(
			produce((state) => {
				if (state.jobList[status].stages && stage) {
					state.jobList[status].data[stage].jobCount -= 1;
					state.jobList[status].data[stage].jobs.splice(jobIndex, 1);
				} else if (!state.jobList[status].stages) {
					state.jobList[status].data.jobCount -= 1;
					state.jobList[status].data.jobs.splice(jobIndex, 1);
				}
			})
		);
	},
	addJobToList: (job: JobWithShare) => {
		if (!job || !job.status || !job.job_document_types) {
			showNotification({
				message: "Auftragsliste konnte nicht aktualisiert werden",
				type: "error",
			});
			return;
		}

		const status = job.status as JobStatusEnum;
		const stage = getJobStage(job);
		set(
			produce((state) => {
				if (state.jobList[status].stages) {
					state.jobList[status].data[stage].jobs.unshift(job);
					state.jobList[status].data[stage].jobCount += 1;
				} else {
					state.jobList[status].data.jobs.unshift(job);
					state.jobList[status].data.jobCount += 1;
				}
			})
		);
	},
});
